import React, { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Eye, EyeOff, ArrowLeft, CheckCircle2 } from "lucide-react";
import RscLogo from "../../assests/logo/rscLogo";
import {
  resetPassword,
  sendMobileOtp,
  verifyInvite,
  verifyMobileOtp,
} from "../../reducers/authentication.slice";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/authContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const wrapperStyle = {
  minHeight: "100vh",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#000",
  overflow: "auto",
  padding: "16px 0",
};

const cardStyle = {
  padding: 24,
  backgroundColor: "#101010",
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,0.52)",
  width: "clamp(320px, 90vw, 480px)",
  boxSizing: "border-box",
};

const titleStyle = {
  fontSize: "1.7rem",
  color: "#fff",
  fontWeight: 700,
  margin: "0 0 6px 0",
};

const subtitleStyle = {
  color: "rgba(255,255,255,0.52)",
  fontFamily: "Inter",
  fontWeight: 300,
  fontSize: "0.8rem",
  margin: 0,
};

const fieldLabelStyle = {
  display: "block",
  color: "#fff",
  marginBottom: 8,
  marginTop: 10,
  letterSpacing: "0.8px",
  fontSize: 14,
};

const validationRowStyle = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 8,
};

const AdminLoginPage = () => {
  const [activeStep, setActiveStep] = useState(
    JSON.parse(sessionStorage.getItem("activeStep")) || 0
  );
  const { showToast } = useContext(AuthContext);
  const [inviteCode, setInviteCode] = useState("");
  const [otp, setOtp] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLengthValid, setIsLengthValid] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasUpperCase, setHasUpperCase] = useState(false);
  const [hasSpecialChar, setHasSpecialChar] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    sessionStorage.setItem("activeStep", activeStep);
  }, [activeStep]);

  const handleVerifyInvite = (e) => {
    e.preventDefault();
    setShowLoader(true);
    const payload = {
      invite_code: inviteCode,
    };
    dispatch(verifyInvite(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        setShowLoader(false);
        return;
      }
      setShowLoader(false);
      if (res.payload?.status === "SUSPENDED") {
        showToast(
          "Your account is suspended. You do not have access to log in.",
          "error"
        );
        return;
      }
      localStorage.setItem("user", JSON.stringify(res?.payload));
      setUserDetails(res?.payload);
      res?.payload?.is_verified
        ? setActiveStep(activeStep + 2)
        : setActiveStep(activeStep + 1);
    });
  };

  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value);
    const lengthValid =
      e.target.value.length >= 8 && e.target.value.length <= 125;
    setIsLengthValid(lengthValid);

    const numberValid = /\d/.test(e.target.value);
    setHasNumber(numberValid);

    const upperCaseValid = /[A-Z]/.test(e.target.value);
    setHasUpperCase(upperCaseValid);

    const specialCharValid = /[!@#$%^&*(),.?":{}|<>]/.test(e.target.value);
    setHasSpecialChar(specialCharValid);
  };

  const handleSetPassword = (e) => {
    e.preventDefault();
    setShowLoader(true);
    const payload = {
      id: userDetails?.id || JSON.parse(sessionStorage.getItem("user"))?.id,
      password: newPassword,
      is_verified: true,
      mode: "SET",
    };
    dispatch(
      resetPassword({
        payload,
        access_token: JSON.parse(sessionStorage.getItem("user"))?.access_token,
      })
    ).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        setShowLoader(false);
        return;
      }
      setShowLoader(false);
      localStorage.setItem("user", JSON.stringify(res?.payload));
      setTimeout(() => {
        if (res?.payload?.role === "MANAGER") {
          navigate("/manager-dashboard");
        } else if (res?.payload?.role === "SUPERADMIN") {
          navigate("/admin-dashboard");
        }
      }, 2000);
    });
  };

  const handleSendMobileOtp = (e) => {
    e.preventDefault();
    setShowLoader(true);
    const payload = {
      id: userDetails?.id || JSON.parse(sessionStorage.getItem("user"))?.id,
      country_code: "+1",
      phone_number: mobileNumber || sessionStorage.getItem("mobileNumber"),
    };
    dispatch(sendMobileOtp(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        setShowLoader(false);
        return;
      }
      setShowLoader(false);
      sessionStorage.setItem("mobileNumber", mobileNumber);
      showToast(
        "An verification code has been sent to your mobile number",
        "success"
      );
      setActiveStep(activeStep + 1);
    });
  };

  const handleVerifyMobileOtp = (e) => {
    e.preventDefault();
    setShowLoader(true);
    const payload = {
      id: userDetails?.id || JSON.parse(sessionStorage.getItem("user"))?.id,
      otp: otp,
    };
    dispatch(verifyMobileOtp(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        setShowLoader(false);
        return;
      }
      setShowLoader(false);
      localStorage.setItem("user", JSON.stringify(res?.payload));
      setTimeout(() => {
        if (res?.payload?.role === "MANAGER") {
          navigate("/manager-dashboard");
        } else if (res?.payload?.role === "SUPERADMIN") {
          navigate("/admin-dashboard");
        }
      }, 2000);
    });
  };

  const PasswordToggle = () => (
    <button
      type="button"
      onClick={handleClickShowPassword}
      aria-label={showPassword ? "Hide password" : "Show password"}
      style={{
        position: "absolute",
        right: 10,
        top: "50%",
        transform: "translateY(-50%)",
        background: "none",
        border: "none",
        cursor: "pointer",
        color: "rgba(255,255,255,0.52)",
        display: "flex",
        alignItems: "center",
        padding: 0,
      }}
    >
      {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
    </button>
  );

  return (
    <div style={wrapperStyle}>
      <div style={cardStyle}>
        {/* Header row: logo + back button */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ padding: 12, backgroundColor: "#fff", borderRadius: 3, width: "fit-content" }}>
            <RscLogo color="black" />
          </div>
          {activeStep >= 1 && (
            <button
              type="button"
              onClick={() => setActiveStep(activeStep - 1)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: "rgba(255,255,255,0.52)",
                cursor: "pointer",
                background: "none",
                border: "none",
                fontFamily: "Inter",
                fontSize: 14,
              }}
            >
              <ArrowLeft size={18} /> Back
            </button>
          )}
        </div>

        {/* Step 0: Invite Code */}
        {activeStep === 0 ? (
          <>
            <p style={titleStyle}>RSC Admin/Manager Access</p>
            <p style={subtitleStyle}>Login or register using your invite for secure access.</p>
            <form onSubmit={handleVerifyInvite}>
              <div style={{ marginTop: 16 }}>
                <Label htmlFor="inviteCode" style={fieldLabelStyle}>Invite Code</Label>
                <Input
                  id="inviteCode"
                  type="text"
                  name="inviteCode"
                  value={inviteCode}
                  placeholder="Invite Code"
                  onChange={(e) => setInviteCode(e.target.value)}
                />
              </div>
              <Button
                disabled={showLoader || inviteCode.length < 6}
                type="submit"
                style={{ width: "100%", marginTop: 16 }}
              >
                Confirm
              </Button>
            </form>
            <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
              <button
                type="button"
                onClick={() => navigate("/login")}
                style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", fontFamily: "Inter", fontSize: 14 }}
              >
                Back to login
              </button>
            </div>
          </>
        ) : activeStep === 1 ? (
          /* Step 1: Set Password */
          <>
            <p style={titleStyle}>Set Your Password?</p>
            <p style={subtitleStyle}>{userDetails?.email}</p>
            <form onSubmit={handleSetPassword}>
              <div style={{ marginTop: 16 }}>
                <Label htmlFor="newPassword" style={fieldLabelStyle}>New Password</Label>
                <div style={{ position: "relative" }}>
                  <Input
                    id="newPassword"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    placeholder="Password"
                    onChange={handleNewPasswordChange}
                    style={{ paddingRight: 36, marginBottom: 8 }}
                  />
                  <PasswordToggle />
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
                <div style={validationRowStyle}>
                  <CheckCircle2 size={18} color={isLengthValid ? "#4CAF50" : "rgba(255,255,255,0.52)"} />
                  <span style={{ color: "rgba(255,255,255,0.52)", fontSize: 13 }}>8 to 125 Characters</span>
                </div>
                <div style={validationRowStyle}>
                  <CheckCircle2 size={18} color={hasNumber ? "#4CAF50" : "rgba(255,255,255,0.52)"} />
                  <span style={{ color: "rgba(255,255,255,0.52)", fontSize: 13 }}>At least 1 number</span>
                </div>
                <div style={validationRowStyle}>
                  <CheckCircle2 size={18} color={hasUpperCase ? "#4CAF50" : "rgba(255,255,255,0.52)"} />
                  <span style={{ color: "rgba(255,255,255,0.52)", fontSize: 13 }}>At least 1 uppercase letter</span>
                </div>
                <div style={validationRowStyle}>
                  <CheckCircle2 size={18} color={hasSpecialChar ? "#4CAF50" : "rgba(255,255,255,0.52)"} />
                  <span style={{ color: "rgba(255,255,255,0.52)", fontSize: 13 }}>At least 1 Special Character</span>
                </div>
              </div>
              <div style={{ marginTop: 16 }}>
                <Label htmlFor="confirmPassword" style={fieldLabelStyle}>Confirm Password</Label>
                <div style={{ position: "relative" }}>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    placeholder="Password"
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={{ paddingRight: 36, marginBottom: 4 }}
                  />
                  <PasswordToggle />
                </div>
                {confirmPassword.length > 0 && newPassword !== confirmPassword && (
                  <p style={{ color: "#f44336", fontSize: 12, margin: "4px 0 0" }}>
                    Password do not match
                  </p>
                )}
              </div>
              <Button
                disabled={
                  showLoader ||
                  !isLengthValid ||
                  !hasNumber ||
                  !hasUpperCase ||
                  newPassword !== confirmPassword
                }
                type="submit"
                style={{ width: "100%", marginTop: 16 }}
              >
                Confirm
              </Button>
            </form>
          </>
        ) : activeStep === 2 ? (
          /* Step 2: Set Up 2FA */
          <>
            <p style={titleStyle}>Set Up 2-Factor {"\n"}Authentication</p>
            <p style={subtitleStyle}>
              We'll send a 6-digit code to your mobile number to verify your identity.
            </p>
            <form onSubmit={handleSendMobileOtp}>
              <div style={{ marginTop: 16, marginBottom: 16 }}>
                <Label htmlFor="mobileNumber" style={fieldLabelStyle}>Mobile Number</Label>
                <Input
                  id="mobileNumber"
                  type="text"
                  name="mobileNumber"
                  value={mobileNumber}
                  placeholder="Mobile Number"
                  onChange={(e) => setMobileNumber(e.target.value)}
                />
              </div>
              <Button
                disabled={showLoader || mobileNumber.length < 1}
                type="submit"
              >
                Send
              </Button>
            </form>
          </>
        ) : (
          /* Step 3: Enter OTP */
          <>
            <p style={titleStyle}>Enter the Verification Code</p>
            <p style={subtitleStyle}>
              A 6-digit code was sent to your mobile number. Please enter it below.
            </p>
            <form onSubmit={handleVerifyMobileOtp}>
              <div style={{ marginTop: 16, marginBottom: 16 }}>
                <Label htmlFor="otp" style={fieldLabelStyle}>Verification Code</Label>
                <Input
                  id="otp"
                  type="number"
                  name="otp"
                  value={otp}
                  placeholder="Verification Code"
                  onChange={(e) => setOtp(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                />
              </div>
              <Button
                disabled={showLoader || otp.length < 1}
                type="submit"
                style={{ width: "100%" }}
              >
                Submit
              </Button>
            </form>
            <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
              <button
                type="button"
                onClick={handleSendMobileOtp}
                style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", fontFamily: "Inter", fontWeight: 300, fontSize: "0.9rem" }}
              >
                Resend verification code
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminLoginPage;
